// Chat API client for plan modification conversations

import { apiGet, apiPost, apiPatch, apiDelete } from "../api-client"

// Types based on backend models
export interface ChatThread {
  id: number
  tenant_id: string
  created_by_email: string
  shared_with_emails: string[]
  well_api14: string
  well_operator: string
  well_field: string
  baseline_plan_id: string
  current_plan_id: string
  openai_thread_id: string
  title: string
  mode: string
  is_active: boolean
  created_at: string
  updated_at: string
  last_message_at: string | null
  message_count: number
  modification_count: number
  permissions: {
    can_view: boolean
    can_edit: boolean
  }
}

export interface ChatMessage {
  id: number
  thread_id: number
  role: "user" | "assistant" | "system"
  content: string
  tool_calls?: any[]
  tool_results?: any[]
  openai_message_id?: string
  openai_run_id?: string
  metadata?: Record<string, any>
  created_at: string
}

export interface PlanModification {
  id: number
  thread_id: number
  message_id: number
  operation_type: string
  operation_payload: any
  source_snapshot_id: string
  result_snapshot_id: string
  risk_score: number
  violations_delta: any
  diff: any
  status: "pending" | "applied" | "rejected"
  applied_by?: number
  applied_at?: string
  created_at: string
}

export interface CreateThreadRequest {
  well_api14: string
  plan_id: string
  title?: string
  share_with_user_ids?: number[]
}

export interface SendMessageRequest {
  content: string
  allow_plan_changes?: boolean
  max_tool_calls?: number
}

export interface ShareThreadRequest {
  user_ids: number[]
  action: "add" | "remove"
}

export interface PaginatedResponse<T> {
  count: number
  next: string | null
  previous: string | null
  results: T[]
}

/**
 * Create a new chat thread for a plan
 */
export async function createChatThread(request: CreateThreadRequest): Promise<ChatThread> {
  return apiPost<ChatThread>("/api/chat/threads/", request)
}

/**
 * List all chat threads (owned or shared)
 */
export async function listChatThreads(): Promise<ChatThread[]> {
  const response = await apiGet<any>("/api/chat/threads/")

  console.log("[v0] listChatThreads response:", response)

  // If response is already an array, return it
  if (Array.isArray(response)) {
    console.log(`[v0] Response is array with ${response.length} threads`)
    return response
  }

  // If response has results property (paginated), return it
  if (response.results && Array.isArray(response.results)) {
    console.log(`[v0] Found ${response.results.length} threads in response.results`)
    return response.results
  }

  console.log("[v0] No threads found in response, returning empty array")
  return []
}

/**
 * Find an existing active thread for a specific well
 */
export async function findThreadForWell(wellApi14: string): Promise<ChatThread | null> {
  try {
    console.log(`[v0] findThreadForWell: Looking for threads with well_api14=${wellApi14}`)

    const threads = await listChatThreads()
    console.log(`[v0] findThreadForWell: Got ${threads.length} total threads`)

    // Log all threads for debugging
    threads.forEach((t) => {
      console.log(`[v0]   Thread ${t.id}: well=${t.well_api14}, active=${t.is_active}, title="${t.title}"`)
    })

    // Find the most recent active thread for this well
    const wellThreads = threads.filter((t) => t.well_api14 === wellApi14 && t.is_active)
    console.log(`[v0] findThreadForWell: Found ${wellThreads.length} matching threads for this well`)

    if (wellThreads.length === 0) {
      console.log("[v0] findThreadForWell: No matching threads found")
      return null
    }

    // Sort by last_message_at or created_at to get the most recent
    wellThreads.sort((a, b) => {
      const aTime = a.last_message_at || a.created_at
      const bTime = b.last_message_at || b.created_at
      return new Date(bTime).getTime() - new Date(aTime).getTime()
    })

    const selectedThread = wellThreads[0]
    console.log(`[v0] findThreadForWell: Selected thread ${selectedThread.id} (most recent)`)
    return selectedThread
  } catch (error) {
    console.error("[v0] Error finding thread for well:", error)
    return null
  }
}

/**
 * Get a specific chat thread
 */
export async function getChatThread(threadId: number): Promise<ChatThread> {
  return apiGet<ChatThread>(`/api/chat/threads/${threadId}/`)
}

/**
 * Update a chat thread (owner only)
 */
export async function updateChatThread(
  threadId: number,
  updates: { title?: string; is_active?: boolean },
): Promise<ChatThread> {
  return apiPatch<ChatThread>(`/api/chat/threads/${threadId}/`, updates)
}

/**
 * Archive a chat thread (owner only)
 */
export async function archiveChatThread(threadId: number): Promise<void> {
  return apiDelete<void>(`/api/chat/threads/${threadId}/`)
}

/**
 * Share or unshare a thread with users (owner only)
 */
export async function shareChatThread(threadId: number, request: ShareThreadRequest): Promise<ChatThread> {
  return apiPost<ChatThread>(`/api/chat/threads/${threadId}/share/`, request)
}

/**
 * List messages in a thread
 */
export async function listChatMessages(
  threadId: number,
  limit = 50,
  offset = 0,
  bustCache = false,
): Promise<ChatMessage[]> {
  const cacheBuster = bustCache ? `&_t=${Date.now()}` : ""
  const response = await apiGet<any>(
    `/api/chat/threads/${threadId}/messages/?limit=${limit}&offset=${offset}${cacheBuster}`,
    bustCache ? { cache: "no-store" } : undefined,
  )

  console.log("[v0] listChatMessages response:", response)

  // Backend returns: { thread_id, messages: [...], pagination: {...} }
  if (Array.isArray(response)) {
    console.log("[v0] Response is array, returning directly")
    return response
  }

  // If response has messages property, return it
  if (response.messages && Array.isArray(response.messages)) {
    console.log(`[v0] Found ${response.messages.length} messages in response.messages`)
    return response.messages
  }

  // Fallback to results for other pagination formats
  if (response.results && Array.isArray(response.results)) {
    console.log(`[v0] Found ${response.results.length} messages in response.results`)
    return response.results
  }

  console.log("[v0] No messages found in response, returning empty array")
  return []
}

/**
 * Send a message in a thread (owner only)
 */
export async function sendChatMessage(
  threadId: number,
  request: SendMessageRequest,
): Promise<{ user_message: ChatMessage; status_url?: string; polling_interval_ms?: number }> {
  return apiPost<{ user_message: ChatMessage; status_url?: string; polling_interval_ms?: number }>(
    `/api/chat/threads/${threadId}/messages/`,
    request,
  )
}

/**
 * Poll for new messages after a specific message ID
 * Used to wait for assistant responses after sending a user message
 */
export async function pollForNewMessages(
  threadId: number,
  afterMessageId: number,
  intervalMs = 1000,
  maxAttempts = 30,
): Promise<ChatMessage | null> {
  console.log(`[v0] Starting poll for assistant response after message ID ${afterMessageId}`)

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    await new Promise((resolve) => setTimeout(resolve, intervalMs))

    try {
      console.log(`[v0] Poll attempt ${attempt + 1}/${maxAttempts}`)

      const messages = await listChatMessages(threadId, 50, 0, true)

      console.log(`[v0] Received ${messages.length} messages:`)
      messages.forEach((m) => {
        console.log(`[v0]   - Message ${m.id}: ${m.role} (created: ${m.created_at})`)
      })

      // Check for assistant message created after user message
      const assistantMessage = messages.find((m) => m.role === "assistant" && m.id > afterMessageId)

      if (assistantMessage) {
        console.log(`[v0] ✅ Found assistant message ${assistantMessage.id}!`)
        console.log(`[v0] Content preview: ${assistantMessage.content.slice(0, 100)}...`)
        return assistantMessage
      }

      console.log(`[v0] No assistant message found yet, continuing...`)
    } catch (error) {
      console.error("[v0] Error polling for messages:", error)
      // Continue polling even on error
    }
  }

  // Timeout - no response after max attempts
  console.error(`[v0] ❌ Polling timed out after ${maxAttempts} attempts`)
  return null
}

/**
 * List modifications in a thread
 */
export async function listPlanModifications(threadId: number): Promise<PlanModification[]> {
  return apiGet<PlanModification[]>(`/api/chat/threads/${threadId}/modifications/`)
}



